import { HelpTooltip } from '@alkem/react-ui-helptooltip';
import classNames from 'classnames';
import { ChangeEvent, PureComponent } from 'react';
import './index.scss';

interface Props {
  id: string;
  options: any[];
  className?: string;
  disabled?: boolean;
  name?: string;
  helpMessage?: string;
  inputClassName?: string;
  label?: string;
  labelSize?: number;
  onChange?: (changeEvent: ChangeEvent<HTMLInputElement>) => void;
  value: any;
  vertical?: boolean;
}

export class Radio extends PureComponent<Props> {
  public static demoProps = [
    {
      id: 'radios1',
      label: 'Radios',
      value: 1,
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
    },
    {
      id: 'radios2',
      label: 'Radios',
      value: 2,
      helpMessage: 'Help !',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      disabled: true,
    },
    {
      id: 'radios3',
      label: 'Radios',
      value: 3,
      helpMessage: 'Help !',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      vertical: true,
    },
    {
      id: 'radios4',
      value: 3,
      options: [
        {
          label: (
            <span>
              Choice 1&nbsp;
              <HelpTooltip id="id-choice-1-uniq" message="Choise 1 help" />
            </span>
          ),
          value: 1,
        },
        { label: <span>Choice 2</span>, value: 2 },
      ],
      vertical: true,
    },
    {
      id: 'radios5',
      label: 'larger label',
      labelSize: 6,
      value: 1,
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
    },
  ];

  public static demoHandlers = {
    onChange: {
      prop: 'value',
      handler: (event) => Number(event.target.value),
    },
  };

  private renderLabel() {
    const { label, labelSize = 4, helpMessage, id } = this.props;
    if (!label) {
      return null;
    }

    return (
      <label
        htmlFor={`InputRadio__help-${id}-helper`}
        className={`InputField__label col-xs-${labelSize}`}
      >
        <span className="InputRadio__label">{label}</span>
        {helpMessage && (
          <span className="InputRadio__help">
            <HelpTooltip
              id={`InputRadio__help-${id}-helper`}
              message={helpMessage}
            />
          </span>
        )}
      </label>
    );
  }

  private renderRadio() {
    const {
      id,
      inputClassName,
      label,
      labelSize = 4,
      options = [],
      value,
      vertical,
      disabled = false,
    } = this.props;
    const name = this.props.name || `InputRadio-${id}`;

    const classes = {
      InputField__input: true,
      [`col-xs-${12 - labelSize}`]: label,
      'InputField__input--vertical': vertical,
    };

    return (
      <span className={classNames(classes)}>
        {options.map((option) => {
          const htmlId = `${name}-${
            option.value ? option.value.id || option.value : option.value
          }`;
          const isChecked = String(option.value) === String(value);
          const optionClasses = {
            InputRadio__option: !disabled,
            'InputRadio__option--disabled': disabled,
            'InputRadio__option--checked': isChecked,
          };

          const iconClasses = {
            InputRadio__icon: true,
            'InputRadio__icon--checked': isChecked,
            'InputRadio__icon--disabled': disabled,
          };

          const onChange = option.onChange || this.props.onChange;

          const tooltipAttributes = option.hasTooltip
            ? { 'data-tip': '', 'data-for': htmlId }
            : {};

          return (
            <div
              className="InputRadio__inputContent"
              key={htmlId}
              {...tooltipAttributes}
            >
              {option.icon ? (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label className={classNames(iconClasses)} htmlFor={htmlId}>
                  <i className={option.icon} />
                </label>
              ) : null}
              <label
                key={htmlId}
                htmlFor={htmlId}
                id={`${htmlId}-label`}
                className={classNames(optionClasses)}
              >
                <input
                  id={htmlId}
                  name={name}
                  type="radio"
                  className={classNames('InputRadio__input', inputClassName)}
                  onChange={onChange}
                  value={String(option.value)}
                  checked={isChecked}
                  disabled={disabled}
                />
                <span className="InputRadio__label">{option.label}</span>
              </label>
            </div>
          );
        })}
      </span>
    );
  }

  public render() {
    const { className } = this.props;
    return (
      <div className={classNames('InputField InputRadio row', className)}>
        {this.renderLabel()}
        {this.renderRadio()}
      </div>
    );
  }
}

export default Radio;
